/**
 * Mount de la galería `/briefings.html` — Fase 7g.1.
 *
 * Flujo:
 *  1. mountChrome con activeSection='briefings'.
 *  2. Inyecta el shell de la galería en `chrome-main`.
 *  3. Carga en paralelo `listBriefings()` y `fetchBriefingMissions()`.
 *     Las misiones se usan para resolver `missionReportId → label`.
 *  4. Renderiza filas ordenadas por `updatedAt` desc.
 *  5. Cablea click/Enter/Espacio en filas → navega a
 *     `/briefing/ficha.html?id=<id>` (vista ficha read-only). El botón
 *     EDITAR de la ficha salta al editor `/briefing.html?id=<id>` si
 *     el user tiene permiso. El botón X (visible solo si el user puede
 *     borrar) intercepta el click y abre el modal.
 */

import type { AuthenticatedPilot } from '@51esc/shared';
import { mountChrome } from '../chrome/index.js';
import { openConfirmModal } from '../confirm-modal/index.js';
import {
  deleteBriefing,
  fetchBriefingMissions,
  fetchBriefingTargets,
  listBriefings,
  type BriefingMission,
} from '../briefing/api.js';
import type { MissionSummary } from '@51esc/shared';
import {
  renderEmptyRow,
  renderErrorRow,
  renderGalleryShell,
  renderRow,
} from './markup.js';

import './briefings-list.css';

export async function mountBriefingsList(): Promise<void> {
  const { contentRoot, user } = await mountChrome({
    activeSection: 'briefings',
    allowAnonymous: true,
  });
  contentRoot.innerHTML = renderGalleryShell(user);

  const tbody = contentRoot.querySelector<HTMLElement>('[data-bgallery-rows]');
  if (!tbody) return;

  await reload(tbody, user);

  // Delegación: click en X borra; click en cualquier otra parte de la
  // fila navega al editor. Enter/Espacio teclados también navegan.
  tbody.addEventListener('click', (ev) => {
    const target = ev.target as HTMLElement;
    const deleteBtn = target.closest<HTMLElement>('[data-bgallery-delete]');
    if (deleteBtn) {
      ev.stopPropagation();
      const id = deleteBtn.dataset['bgalleryDelete'];
      if (id) void onDeleteClick(id, tbody, user);
      return;
    }
    const tr = target.closest<HTMLElement>('[data-briefing-id]');
    if (!tr) return;
    navigateToView(tr.dataset['briefingId']);
  });
  tbody.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter' && ev.key !== ' ') return;
    const tr = (ev.target as HTMLElement).closest<HTMLElement>(
      '[data-briefing-id]',
    );
    if (!tr) return;
    ev.preventDefault();
    navigateToView(tr.dataset['briefingId']);
  });
}

async function reload(
  tbody: HTMLElement,
  user: AuthenticatedPilot | null,
): Promise<void> {
  tbody.innerHTML = `<tr><td colspan="7" class="bgallery-loading">cargando…</td></tr>`;

  // Carga en paralelo: necesitamos la lista de mission_reports (legacy)
  // y la de campaigns+missions (Fase 8e+) para resolver el label de
  // "Misión asignada" en la galería sea cual sea el linker.
  const [briefingsRes, legacyMissions, targets] = await Promise.all([
    listBriefings(),
    fetchBriefingMissions(),
    fetchBriefingTargets(),
  ]);

  const missionsCache = new Map<string, BriefingMission>();
  for (const m of legacyMissions) missionsCache.set(m.id, m);
  const declaredMissionsCache = new Map<string, MissionSummary>();
  for (const m of targets.missions) declaredMissionsCache.set(m.id, m);

  if (!briefingsRes.ok) {
    tbody.innerHTML = renderErrorRow(briefingsRes.message);
    return;
  }

  const list = briefingsRes.data
    .slice()
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  if (list.length === 0) {
    tbody.innerHTML = renderEmptyRow(user);
    return;
  }

  tbody.innerHTML = list
    .map((b) =>
      renderRow(b, missionsCache, declaredMissionsCache, user),
    )
    .join('');
}

async function onDeleteClick(
  id: string,
  tbody: HTMLElement,
  user: AuthenticatedPilot | null,
): Promise<void> {
  // Defensa: el botón X no se renderiza para anónimos, pero si llegara
  // aquí sin sesión cortamos — el borrado es solo para usuarios logueados.
  if (user === null) return;
  const confirmed = await openConfirmModal({
    title: 'Borrar briefing',
    message:
      `¿Seguro que quieres borrar este briefing? Se eliminarán también ` +
      `sus fotos. Esta acción no se puede deshacer.`,
    confirmLabel: 'Borrar',
    danger: true,
  });
  if (!confirmed) return;

  const res = await deleteBriefing(id);
  if (!res.ok) {
    await openConfirmModal({
      title: 'Error al borrar',
      message: res.message,
      confirmLabel: 'Cerrar',
      cancelLabel: 'Cerrar',
    });
    return;
  }
  await reload(tbody, user);
}

function navigateToView(id: string | undefined): void {
  if (!id) return;
  window.location.href = `/briefing/ficha.html?id=${encodeURIComponent(id)}`;
}
